import React, { useState } from 'react';
import { Box } from '@mui/material';
import { Chip, IconButton, Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import { SideNavigation } from '@dreebit-registry/vsm-microservice-components';
import { SettingsFilledRounded, CloseRounded, ChevronRightRounded } from '@mui-symbols-material/w300';
import AssetProductImage from '../../components/AssetProductImage';
import { KeyValueInformation, getStatusChipColors, getStatusChipLabel } from '../../components/AssetNameplate';
import {
    DashboardPreviewContent,
    ScenarioSwitcher,
    accentFor,
    appNameFor,
    useCompactWidth,
    type Scenario,
} from './dashboardPreview';
import { IndustryType } from '../../hooks/useIndustryType';

const PREVIEW_BASE = '/assets/dashboardPreview';

/* Static nameplate mirroring AssetNameplate (no GraphQL). */
const PreviewNameplate = ({ scenario }: { scenario: Scenario }) => {
    const { t } = useTranslation();
    const isSystem = scenario.dashboard === 'system';
    const industryType = scenario.segment === 'semiconductor' ? IndustryType.SEMICONDUCTOR : IndustryType.INDUSTRY;
    const connectionStatus = scenario.state === 'connected' ? 'RUNNING' : undefined;
    const title = isSystem ? 'Production system PS-3' : 'Vacuum pump VP-12';

    return (
        <Box display="flex" flexDirection="column">
            <Box sx={{ pb: '12px' }} alignSelf="center">
                <AssetProductImage industryType={industryType} style={{ width: 160, height: 160 }} />
            </Box>
            <Typography level="titleLargeBold" textAlign="center">
                {title}
            </Typography>
            <Box sx={{ padding: '12px 8px' }}>
                {isSystem ? (
                    <>
                        <KeyValueInformation title={t('System type')} value={t('Group of pumps')} />
                        <KeyValueInformation title={t('Assets')} value="6" />
                        <KeyValueInformation title={t('Location')} value="Building B" />
                    </>
                ) : (
                    <>
                        <KeyValueInformation title={t('Serial No.')} value="771-3300" />
                        <KeyValueInformation title={t('Product code')} value="MV-0063-B" />
                        <KeyValueInformation title={t('Manufacturer')} value="Busch" />
                        {scenario.segment === 'semiconductor' && (
                            <KeyValueInformation title={t('Chamber')} value="Fab 2 · Chamber C-North" />
                        )}
                    </>
                )}
            </Box>
            {connectionStatus && (() => {
                const { bg, text } = getStatusChipColors(connectionStatus);
                return (
                    <Chip sx={{ width: '100%', textAlign: 'center', backgroundColor: bg, color: text }}>
                        {getStatusChipLabel(t, connectionStatus)}
                    </Chip>
                );
            })()}
        </Box>
    );
};

/* Scenario-driven nav items mirroring Asset-/SystemDetailNavigation. Links stay within the preview wildcard. */
const useNavItems = (scenario: Scenario) => {
    const { t } = useTranslation();
    const connected = scenario.state === 'connected';
    if (scenario.dashboard === 'system') {
        return [
            { label: t('Dashboard'), to: `${PREVIEW_BASE}/dashboard` },
            ...(connected
                ? [
                      { label: t('Maintenance'), to: `${PREVIEW_BASE}/maintenance` },
                      { label: t('Alerts'), to: `${PREVIEW_BASE}/alerts` },
                  ]
                : []),
            { label: t('Documents'), to: `${PREVIEW_BASE}/files` },
        ];
    }
    return [
        { label: t('Dashboard'), to: `${PREVIEW_BASE}/dashboard` },
        ...(connected
            ? [
                  {
                      label: t('Condition Monitoring'),
                      subItems: [
                          { label: t('Telemetry'), to: `${PREVIEW_BASE}/conditionMonitoring/telemetry` },
                          { label: t('Maintenance'), to: `${PREVIEW_BASE}/conditionMonitoring/maintenance` },
                          { label: t('Alerts'), to: `${PREVIEW_BASE}/conditionMonitoring/alerts` },
                      ],
                  },
              ]
            : []),
        { label: t('Specifications'), to: `${PREVIEW_BASE}/specifications` },
        ...(!connected ? [{ label: t('Service Reminders'), to: `${PREVIEW_BASE}/serviceReminders` }] : []),
        { label: t('Documents'), to: `${PREVIEW_BASE}/files` },
        { label: t('Event History'), to: `${PREVIEW_BASE}/events` },
    ];
};

/* Lightweight static breadcrumb trail (visual parity, no router data shape). */
const PreviewBreadcrumbs = ({ scenario }: { scenario: Scenario }) => {
    const { t } = useTranslation();
    const crumbs = [
        scenario.dashboard === 'system' ? t('Systems') : t('Assets'),
        scenario.dashboard === 'system' ? 'Production system PS-3' : 'Vacuum pump VP-12',
        scenario.dashboard === 'system' && scenario.systemKind === 'plc' ? t('IoT Portal') : t('Dashboard'),
    ];
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', pb: 2 }}>
            {crumbs.map((c, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <ChevronRightRounded sx={{ fontSize: 16, color: '#bbb' }} />}
                    <Typography level="bodySmall" sx={{ color: i === crumbs.length - 1 ? '#333' : '#888' }}>
                        {c}
                    </Typography>
                </React.Fragment>
            ))}
        </Box>
    );
};

/* Floating, hideable scenario switcher overlay. */
const FloatingSwitcher = ({ scenario, onChange }: { scenario: Scenario; onChange: <K extends keyof Scenario>(key: K, value: Scenario[K]) => void }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(true);

    if (!open) {
        return (
            <IconButton
                variant="solid"
                color="neutral"
                onClick={() => setOpen(true)}
                sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 2000, borderRadius: '50%', width: 48, height: 48, boxShadow: 'md', backgroundColor: '#333' }}
            >
                <SettingsFilledRounded />
            </IconButton>
        );
    }

    return (
        <Box
            sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 2000,
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                borderRadius: '10px',
                boxShadow: '0 8px 30px rgba(0,0,0,.18)',
                p: '16px 18px',
                maxWidth: 'min(92vw, 880px)',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: '12px' }}>
                <Typography level="titleSmall" sx={{ color: '#333' }}>
                    {t('Scenario preview')}
                </Typography>
                <Typography level="bodySmall" sx={{ color: '#999', ml: '8px' }}>
                    {t('Mockup · hardcoded data')}
                </Typography>
                <IconButton size="sm" variant="plain" color="neutral" onClick={() => setOpen(false)} sx={{ ml: 'auto' }}>
                    <CloseRounded />
                </IconButton>
            </Box>
            <ScenarioSwitcher scenario={scenario} onChange={onChange} />
        </Box>
    );
};

const DashboardPreviewScreen = () => {
    const [scenario, setScenario] = useState<Scenario>({
        dashboard: 'asset',
        segment: 'industry',
        role: 'admin',
        state: 'connected',
        systemKind: 'custom',
        variant: 'B',
    });
    const onChange = <K extends keyof Scenario>(key: K, value: Scenario[K]) => setScenario((s) => ({ ...s, [key]: value }));

    // Hero (B) and Baseline (A) are fully adaptive. Dense/Sectioned (C/D) freeze Role/State/
    // System-type at Admin · Connected but keep the live Segment. Derived at render, so
    // switching back restores the prior selections.
    const effectiveScenario: Scenario =
        scenario.dashboard === 'asset' && (scenario.variant === 'C' || scenario.variant === 'D')
            ? { ...scenario, role: 'admin', state: 'connected', systemKind: 'custom' }
            : scenario;

    // Compact mode tracks the actual content-area width (below ~900px CSS px → dense widgets).
    const { ref: contentRef, compact } = useCompactWidth(900);

    const accent = accentFor(effectiveScenario.segment);
    const appName = appNameFor(effectiveScenario.segment);
    const navItems = useNavItems(effectiveScenario);

    return (
        <div className="flex-row" style={{ flexGrow: 1 }}>
            {/* Left detail navigation */}
            <Box
                sx={{
                    width: 320,
                    borderRight: '1px solid #ccc',
                    margin: '12px 0',
                    padding: '0 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 'none',
                }}
            >
                <PreviewNameplate scenario={effectiveScenario} />
                <Box sx={{ pt: '8px' }}>
                    {/* key forces SideNavigation to recompute when the item set changes */}
                    <SideNavigation key={`nav-${effectiveScenario.dashboard}-${effectiveScenario.state}`} navItems={navItems} />
                </Box>
            </Box>

            {/* Content */}
            <Box ref={contentRef} sx={{ flex: 3, padding: '12px 0 12px 12px', minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <PreviewBreadcrumbs scenario={effectiveScenario} />
                    <Box sx={{ backgroundColor: accent, color: '#fff', px: '10px', py: '3px', borderRadius: '4px' }}>
                        <Typography level="bodySmallBold" sx={{ color: '#fff' }}>
                            {appName}
                        </Typography>
                    </Box>
                </Box>

                <DashboardPreviewContent scenario={effectiveScenario} compact={compact} />
            </Box>

            <FloatingSwitcher scenario={effectiveScenario} onChange={onChange} />
        </div>
    );
};

export default DashboardPreviewScreen;
